<template>
  <div class="admin-section">
    <div class="section-header">
      <h2 class="section-title">Audit Logs</h2>
      <div class="header-actions">
        <Button @click="loadLogs(true)" :disabled="isLoading" variant="default" size="sm">
          <span v-if="!isLoading">Refresh</span><span v-else>Loading...</span>
        </Button>
        <Button @click="exportCsv" :disabled="isExporting" variant="outline" size="sm">
          <span v-if="!isExporting">Export CSV</span><span v-else>Exporting...</span>
        </Button>
      </div>
    </div>

    <div class="filters">
      <select v-model="filters.actor_type" @change="loadLogs(true)">
        <option value="">All actor types</option>
        <option value="user">User</option>
        <option value="admin">Admin</option>
        <option value="researcher">Researcher</option>
        <option value="anonymous">Anonymous</option>
        <option value="system">System</option>
      </select>
      <select v-model="filters.category" @change="loadLogs(true)">
        <option value="">All categories</option>
        <option value="auth">Auth</option>
        <option value="access">Access</option>
        <option value="mutation">Mutation</option>
        <option value="authz_denied">Authz denied</option>
      </select>
      <select v-model="filters.outcome" @change="loadLogs(true)">
        <option value="">All outcomes</option>
        <option value="success">Success</option>
        <option value="failure">Failure</option>
        <option value="denied">Denied</option>
      </select>
      <input v-model="filters.actor_email" @keyup.enter="loadLogs(true)" placeholder="Actor email contains..." />
      <input v-model="filters.action" @keyup.enter="loadLogs(true)" placeholder="Action (e.g. user.role.change)" />
      <input v-model="filters.entity_type" @keyup.enter="loadLogs(true)" placeholder="Entity type" />
      <input v-model="filters.entity_id" @keyup.enter="loadLogs(true)" placeholder="Entity id" />
      <input v-model="filters.q" @keyup.enter="loadLogs(true)" placeholder="Search action/path/reason" />
      <input type="datetime-local" v-model="filters.start" @change="loadLogs(true)" />
      <input type="datetime-local" v-model="filters.end" @change="loadLogs(true)" />
      <Button @click="resetFilters" variant="outline" size="sm">Clear</Button>
    </div>

    <div v-if="error" class="message error">{{ error }}</div>

    <div v-if="isLoading && logs.length === 0" class="loading-state">Loading audit logs...</div>
    <div v-else-if="logs.length === 0" class="empty-state">No audit log entries match the filters.</div>

    <table v-else class="audit-table">
      <thead>
        <tr>
          <th>Time (UTC)</th>
          <th>Actor</th>
          <th>Action</th>
          <th>Category</th>
          <th>Entity</th>
          <th>Outcome</th>
          <th>Path</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in logs" :key="row.id" @click="selected = row" class="audit-row">
          <td>{{ formatTime(row.event_time) }}</td>
          <td>
            <span class="actor-type" :class="row.actor_type">{{ row.actor_type }}</span>
            <span v-if="row.actor_email">{{ row.actor_email }}</span>
            <span v-else-if="row.actor_id">#{{ row.actor_id }}</span>
            <span v-else class="muted">—</span>
          </td>
          <td><code>{{ row.action }}</code></td>
          <td>{{ row.category }}</td>
          <td>
            <template v-if="row.entity_type">
              {{ row.entity_type }}<span v-if="row.entity_id">#{{ row.entity_id }}</span>
            </template>
            <span v-else class="muted">—</span>
          </td>
          <td>
            <span class="outcome" :class="row.outcome">{{ row.outcome }}</span>
          </td>
          <td class="path-cell"><code>{{ row.request_method }} {{ row.request_path }}</code></td>
        </tr>
      </tbody>
    </table>

    <div class="pagination" v-if="logs.length">
      <Button @click="loadMore" :disabled="!hasMore || isLoading" variant="outline" size="sm">
        <span v-if="hasMore">Load more</span><span v-else>End of results</span>
      </Button>
      <span class="muted">{{ logs.length }} loaded</span>
    </div>

    <!-- Detail panel -->
    <div v-if="selected" class="detail-overlay" @click.self="selected = null">
      <div class="detail-panel">
        <div class="detail-header">
          <h3>Audit log #{{ selected.id }}</h3>
          <button @click="selected = null" class="close-btn">×</button>
        </div>
        <div class="detail-body">
          <div class="detail-row"><strong>Time:</strong> {{ selected.event_time }}</div>
          <div class="detail-row"><strong>Actor:</strong>
            {{ selected.actor_type }}
            <span v-if="selected.actor_email"> — {{ selected.actor_email }}</span>
            <span v-if="selected.actor_id"> (id={{ selected.actor_id }})</span>
          </div>
          <div class="detail-row"><strong>Action:</strong> <code>{{ selected.action }}</code></div>
          <div class="detail-row"><strong>Category:</strong> {{ selected.category }}</div>
          <div class="detail-row"><strong>Outcome:</strong> {{ selected.outcome }}</div>
          <div v-if="selected.failure_reason" class="detail-row">
            <strong>Failure reason:</strong> {{ selected.failure_reason }}
          </div>
          <div v-if="selected.entity_type" class="detail-row">
            <strong>Entity:</strong> {{ selected.entity_type }}<span v-if="selected.entity_id">#{{ selected.entity_id }}</span>
          </div>
          <div v-if="selected.target_actor_type" class="detail-row">
            <strong>Target:</strong> {{ selected.target_actor_type }}<span v-if="selected.target_actor_id">#{{ selected.target_actor_id }}</span>
          </div>
          <div class="detail-row"><strong>Request:</strong>
            <code>{{ selected.request_method }} {{ selected.request_path }}</code>
            <span v-if="selected.status_code"> ({{ selected.status_code }})</span>
          </div>
          <div class="detail-row"><strong>IP:</strong> {{ selected.ip_address || '—' }}</div>
          <div class="detail-row"><strong>User-agent:</strong>
            <span class="ua">{{ selected.user_agent || '—' }}</span>
          </div>
          <div class="detail-row"><strong>Request id:</strong> <code>{{ selected.request_id || '—' }}</code></div>

          <div v-if="selected.changes" class="detail-block">
            <strong>Changes:</strong>
            <pre>{{ formatJson(selected.changes) }}</pre>
          </div>
          <div v-if="selected.meta" class="detail-block">
            <strong>Meta:</strong>
            <pre>{{ formatJson(selected.meta) }}</pre>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api'
import { Button } from '@/components/ui/button'
import { STORAGE_KEYS } from '@/constants'

const EMPTY_FILTERS = () => ({
  actor_type: '',
  category: '',
  outcome: '',
  actor_email: '',
  action: '',
  entity_type: '',
  entity_id: '',
  q: '',
  start: '',
  end: ''
})

export default {
  name: 'AuditLogsManagement',
  components: { Button },
  data() {
    return {
      logs: [],
      isLoading: false,
      isExporting: false,
      error: '',
      filters: EMPTY_FILTERS(),
      cursor: null,
      hasMore: false,
      selected: null,
      pageLimit: 50
    }
  },
  mounted() {
    this.loadLogs(true)
  },
  methods: {
    buildParams(extra = {}) {
      const params = { limit: this.pageLimit }
      Object.keys(this.filters).forEach((k) => {
        const v = this.filters[k]
        if (v !== '' && v !== null && v !== undefined) params[k] = v
      })
      return { ...params, ...extra }
    },
    async loadLogs(reset = false) {
      this.error = ''
      this.isLoading = true
      try {
        if (reset) {
          this.logs = []
          this.cursor = null
          this.hasMore = false
        }
        const params = this.buildParams()
        if (!reset && this.cursor) {
          params.cursor_time = this.cursor.cursor_time
          params.cursor_id = this.cursor.cursor_id
        }
        const res = await api.get('/api/admin/audit-logs/', { params })
        const items = res.data.items || []
        this.logs = reset ? items : this.logs.concat(items)
        this.cursor = res.data.next_cursor
        this.hasMore = !!res.data.has_more
      } catch (e) {
        this.error = e.response?.data?.detail || e.message || 'Failed to load audit logs'
      } finally {
        this.isLoading = false
      }
    },
    loadMore() {
      if (this.hasMore && !this.isLoading) this.loadLogs(false)
    },
    resetFilters() {
      this.filters = EMPTY_FILTERS()
      this.loadLogs(true)
    },
    async exportCsv() {
      this.isExporting = true
      try {
        const params = this.buildParams()
        delete params.limit
        const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)
        const qs = new URLSearchParams(params).toString()
        const baseURL = api.defaults.baseURL
        const url = `${baseURL}/api/admin/audit-logs/export.csv?${qs}`
        const res = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        })
        if (!res.ok) throw new Error(`Export failed: ${res.status}`)
        const blob = await res.blob()
        const objectUrl = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = objectUrl
        a.download = `audit-logs-${new Date().toISOString()}.csv`
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(objectUrl)
      } catch (e) {
        this.error = e.message || 'Export failed'
      } finally {
        this.isExporting = false
      }
    },
    formatTime(iso) {
      if (!iso) return ''
      try {
        return new Date(iso).toISOString().replace('T', ' ').replace('Z', '')
      } catch {
        return iso
      }
    },
    formatJson(value) {
      try {
        return JSON.stringify(value, null, 2)
      } catch {
        return String(value)
      }
    }
  }
}
</script>

<style scoped>
.admin-section { margin-bottom: 3rem; }
.section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; }
.section-title { font-size: 1.5rem; font-weight: 600; color: #1e293b; margin: 0; }
.header-actions { display: flex; gap: 0.5rem; }

.filters {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: #f8fafc;
  border-radius: 0.5rem;
}
.filters input, .filters select {
  padding: 0.4rem 0.6rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  font-size: 0.85rem;
  background: #fff;
}

.message.error { color: #b91c1c; padding: 0.5rem; background: #fee2e2; border-radius: 0.375rem; margin-bottom: 0.5rem; }
.loading-state, .empty-state { padding: 1rem; color: #64748b; }

.audit-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
.audit-table th, .audit-table td {
  text-align: left;
  padding: 0.5rem 0.6rem;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: top;
}
.audit-table th { background: #f1f5f9; color: #334155; font-weight: 600; }
.audit-row { cursor: pointer; }
.audit-row:hover { background: #f8fafc; }

.actor-type {
  display: inline-block;
  padding: 0.1rem 0.4rem;
  border-radius: 0.25rem;
  font-size: 0.7rem;
  font-weight: 600;
  margin-right: 0.4rem;
  background: #e2e8f0;
  color: #334155;
  text-transform: uppercase;
}
.actor-type.admin { background: #fee2e2; color: #b91c1c; }
.actor-type.user { background: #dbeafe; color: #1e3a8a; }
.actor-type.researcher { background: #dcfce7; color: #166534; }
.actor-type.anonymous { background: #f1f5f9; color: #475569; }

.outcome {
  display: inline-block;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
}
.outcome.success { background: #dcfce7; color: #166534; }
.outcome.failure { background: #fee2e2; color: #b91c1c; }
.outcome.denied { background: #fef3c7; color: #92400e; }

.path-cell { max-width: 280px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.muted { color: #94a3b8; }

.pagination { display: flex; align-items: center; gap: 0.75rem; margin-top: 1rem; }

.detail-overlay {
  position: fixed; inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000;
  padding: 1rem;
}
.detail-panel {
  background: #fff; border-radius: 0.75rem; max-width: 720px; width: 100%;
  max-height: 90vh; overflow: auto; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.2);
}
.detail-header { display: flex; justify-content: space-between; align-items: center; padding: 1rem 1.25rem; border-bottom: 1px solid #e2e8f0; }
.detail-header h3 { margin: 0; font-size: 1.1rem; }
.close-btn { background: none; border: none; font-size: 1.5rem; cursor: pointer; color: #64748b; }
.detail-body { padding: 1rem 1.25rem; }
.detail-row { padding: 0.3rem 0; font-size: 0.9rem; }
.detail-row code, .audit-table code { background: #f1f5f9; padding: 0.05rem 0.35rem; border-radius: 0.25rem; font-size: 0.8rem; }
.detail-block { margin-top: 0.75rem; }
.detail-block pre {
  background: #0f172a; color: #e2e8f0;
  padding: 0.75rem; border-radius: 0.5rem;
  font-size: 0.75rem; max-height: 280px; overflow: auto;
}
.ua { font-family: monospace; font-size: 0.75rem; word-break: break-all; }
</style>
